import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Logo } from './Logo';
import { AUTH_CONNECTION } from '../lib/auth';

// Renders children only when the user is authenticated. While Auth0 resolves the
// session it shows a spinner; when signed out it shows a branded sign-in card.
// Used only when auth is enabled (App wraps it in Auth0Provider).
export const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, isAuthenticated, error, loginWithRedirect } = useAuth0();

  if (isLoading) return <Splash>Loading…</Splash>;

  if (error) {
    return (
      <Splash>
        <p className='mb-3 text-sm text-[color:var(--color-danger)]'>
          Sign-in failed: {error.message}
        </p>
        <SignInButton
          onClick={() => loginWithRedirect({ authorizationParams: connectionParams() })}
        />
      </Splash>
    );
  }

  if (!isAuthenticated) {
    return (
      <Splash>
        <p className='mb-1 text-sm text-on-surface-secondary'>Sign in with your DAG account.</p>
        <p className='mb-5 text-xs text-on-surface-quaternary'>
          Access is limited to approved @dag.com users.
        </p>
        <SignInButton
          onClick={() => loginWithRedirect({ authorizationParams: connectionParams() })}
        />
      </Splash>
    );
  }

  return <>{children}</>;
};

const connectionParams = () => (AUTH_CONNECTION ? { connection: AUTH_CONNECTION } : {});

const Splash: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className='flex min-h-screen items-center justify-center bg-surface-secondary px-4'>
    <div
      className='w-full max-w-sm rounded-2xl border border-theme-border bg-surface p-8 text-center'
      style={{ boxShadow: 'var(--color-card-edge)' }}
    >
      <div className='mb-5 flex items-center justify-center gap-2.5'>
        <Logo className='h-7 w-auto text-on-surface' />
        <span
          className='text-xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: "'Spectral', Georgia, serif" }}
        >
          Canon
        </span>
      </div>
      {children}
    </div>
  </div>
);

const SignInButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className='w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors'
    style={{ background: 'var(--color-primary)' }}
  >
    Sign in
  </button>
);
